import { useGetOne } from "@mb-pro-ui/utils";
import type {
  Customer,
  CustomerUser,
  NotifiablePerson,
  Partition,
  Zone,
} from "./types";

type WithChildren<K extends string, T> = Customer & { [key in K]?: T[] };

const titleFields = ["account", "name"];

export function useCustomerZones(id: string) {
  return useGetOne<WithChildren<"zones", Zone>>("alarm/customers", id, {
    include: { zones: { "zone-type": {}, "sensor-type": {} } },
    fields: {
      customers: [...titleFields, "+zones"],
      zones: ["number", "place", "temporary-inactive-ts", "zone-type", "sensor-type"],
      "zone-types": ["localized-description"],
      "sensor-types": ["localized-description"],
    },
  });
}

export function useCustomerPartitions(id: string) {
  return useGetOne<WithChildren<"partitions", Partition>>("alarm/customers", id, {
    include: { partitions: {} },
    fields: {
      customers: [...titleFields, "+partitions"],
      partitions: ["number", "description"],
    },
  });
}

export function useCustomerUsers(id: string) {
  return useGetOne<WithChildren<"users", CustomerUser>>("alarm/customers", id, {
    include: { users: {} },
    fields: {
      customers: [...titleFields, "+users"],
      users: ["number", "name", "role", "phone"],
    },
  });
}

export function useCustomerNotifiables(id: string) {
  return useGetOne<WithChildren<"notifiable-persons", NotifiablePerson>>(
    "alarm/customers",
    id,
    {
      include: { "notifiable-persons": {} },
      fields: {
        customers: [...titleFields, "+notifiable-persons"],
        "notifiable-persons": ["idx", "name", "role", "phone", "mobile", "email", "remark"],
      },
    },
  );
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

export function byKey<T>(key: (item: T) => string | number | null | undefined) {
  return (a: T, b: T) => {
    const ka = key(a);
    const kb = key(b);
    if (typeof ka === "number" && typeof kb === "number") {
      return ka - kb;
    }
    return collator.compare(String(ka ?? ""), String(kb ?? ""));
  };
}
